import { FormikActions } from 'formik';
import React from 'react';
import styled from 'styled-components';
import Form from '../../../app/common/components/Form';
import EmailField from '../../../app/common/fields/email/EmailField';
import {
    getChallengeData,
    destroyChallenge,
    languageMap,
    IChallengeData,
} from '../../../utils/challengerMain';
import { IUpdateEmailRequest } from './updateEmailRequest';
import { ValidatorRule } from '../../../app/common/validator/validatorRule.constants';
import { FormManditoryAstrixMessage } from '../../../app/common/components/FormManitoryMessage';
import { AccountSecurityFormType } from '../AccountSecurity';
import { ISendEmailOtpPayload } from '../verifyEmail/verifyEmailRequest';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';
import { getEmailFAQUrl, setNewEmail } from '../verifyEmail/verifyEmailUtils';
import { CheckboxWrapper } from '../updatePassword/UpdatePasswordForm';
import CheckboxField from '../../../app/common/fields/CheckboxField';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { RegionHelper } from '../../../utils/RegionHelper';

const FieldWrapper = styled.div`
    & > div {
        margin-top: 50px;
        margin-bottom: 40px;
    }
`;

interface IUpdateEmailFormValues {
    email: string;
    existingEmail: string;
    signOutFromAllDevices: boolean;
}

export type UpdateEmailFormProps = IUpdateEmailFormStateProps & IUpdateEmailFormDispatchProps;

export interface IUpdateEmailFormStateProps {
    challengerPid: string;
    clientId: string;
    member: any;
    isSubmitting: boolean;
    updateEmailValidatorRules: ValidatorRule[];
    passwordValid: boolean;
    locale: string;
    emailVerificationFAQUrls: string;
    supportPageUrl: string;
    sessionRevocationEnabled: boolean;
}

export interface IUpdateEmailFormDispatchProps {
    updateEmail: (values: IUpdateEmailRequest) => any;
    closeAccountSecurityOverlay: () => any;
    changeAccountSecurityOverlay: (content: AccountSecurityFormType | undefined) => any;
    setVerifyingEmail: (email: string) => void;
    validatePassword: (payload: ISendEmailOtpPayload) => void;
    setPasswordValidFalse: () => void;
    setVerifyEmailFromUpdateEmail: () => void;
    setSessionRevoke: (signOutFromAllDevices: boolean) => void;
}

export interface IUpdateEmailState {
    initialValues: IUpdateEmailFormValues;
    challengeData?: IChallengeData;
}

export class UpdateEmailForm extends React.Component<UpdateEmailFormProps, IUpdateEmailState> {
    constructor(props: UpdateEmailFormProps) {
        super(props);

        const { member } = this.props;

        const initialValues: IUpdateEmailFormValues & { emailVerified: boolean } = {
            email: '',
            existingEmail: member.email,
            emailVerified: member.emailVerified,
            signOutFromAllDevices: false,
        };

        this.state = {
            initialValues,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.renderFormFields = this.renderFormFields.bind(this);
        this.renderExtraFields = this.renderExtraFields.bind(this);
        this.challengerReadyCallback = this.challengerReadyCallback.bind(this);
    }

    public render() {
        const {
            isSubmitting,
            challengerPid,
            member: { emailVerified },
        } = this.props;
        const { initialValues } = this.state;

        return (
            <Form
                buttonTextLabel="button.send_veri_code"
                value={initialValues}
                handleSubmit={this.handleSubmit}
                renderFormFields={this.renderFormFields}
                renderExtraFields={this.renderExtraFields}
                loading={isSubmitting}
                challengerPid={challengerPid}
                saveButtonProps={{
                    qaId: 'submit-update-email',
                    width: '100%',
                }}
            />
        );
    }

    componentDidUpdate(prevProps: IUpdateEmailFormStateProps & IUpdateEmailFormDispatchProps) {
        const {
            passwordValid,
            changeAccountSecurityOverlay,
            setPasswordValidFalse,
            setVerifyEmailFromUpdateEmail,
            challengerPid,
        } = this.props;
        if (passwordValid && !prevProps.passwordValid) {
            destroyChallenge(challengerPid);
            setPasswordValidFalse();
            setVerifyEmailFromUpdateEmail();
            changeAccountSecurityOverlay(AccountSecurityFormType.VERIFY_EMAIL);
        }
    }

    private challengerReadyCallback() {
        const { challengerPid } = this.props;
        this.setState({
            challengeData: getChallengeData(challengerPid),
        });
    }

    private handleSubmit(
        values: IUpdateEmailFormValues,
        actions: FormikActions<IUpdateEmailFormValues>,
    ) {
        const {
            member,
            challengerPid,
            setVerifyingEmail,
            setSessionRevoke,
            validatePassword,
            locale,
            emailVerificationFAQUrls,
            supportPageUrl,
        } = this.props;
        const language = languageMap[locale as SupportedLanguage];
        if (values.email) {
            setNewEmail(values.email);
            const challengeData = getChallengeData(challengerPid);
            // Added support for the HelpPage link in the EU region and set a fallback to the Ichiba EN FAQ page instead of '/'.
            const helpPageLink =
                (RegionHelper.isEU()
                    ? supportPageUrl
                    : getEmailFAQUrl(emailVerificationFAQUrls, locale)) ??
                'https://ichiba.faq.rakuten.net/detail/000007260';
            this.setState({ challengeData }, () => {
                if (this.state.challengeData) {
                    validatePassword({
                        email: values.email.toLowerCase(),
                        language,
                        challengeData: this.state.challengeData,
                        helpPageLink,
                    });
                }
                setVerifyingEmail(values.email.toLowerCase());
                setSessionRevoke(values.signOutFromAllDevices);
            });

            actions.resetForm({
                email: values.email.toLowerCase(),
                existingEmail: member.email.toLowerCase(),
                signOutFromAllDevices: false,
            });
        }
    }

    private renderFormFields(values: IUpdateEmailFormValues) {
        const { member, updateEmailValidatorRules } = this.props;

        return (
            <FieldWrapper>
                <FormManditoryAstrixMessage />

                <EmailField
                    id="existingEmail"
                    labelId="label.current_email"
                    value={member.email}
                    required={true}
                    disabled={true}
                    skipValidator={true}
                    validatorRules={updateEmailValidatorRules}
                    qaId="current-email"
                />
                <EmailField
                    id="email"
                    labelId="label.new_email"
                    value={values.email}
                    required={true}
                    qaId="new-email"
                    currentEmail={member.email}
                    validatorRules={updateEmailValidatorRules}
                />
            </FieldWrapper>
        );
    }

    private renderExtraFields(values: IUpdateEmailFormValues) {
        const { sessionRevocationEnabled } = this.props;
        return (
            <React.Fragment>
                {sessionRevocationEnabled && (
                    <CheckboxWrapper>
                        <CheckboxField
                            id="signOutFromAllDevices"
                            label="revoke.session.button.text"
                            value={values.signOutFromAllDevices}
                            tooltipText={TranslationHelpers.getMessageDescriptor(
                                'tooltip.revoke_session',
                            )}
                        />
                    </CheckboxWrapper>
                )}
            </React.Fragment>
        );
    }
}
